/* =============================================================================
 * Tiny fetch wrapper shared by both apps.
 *
 * Backend contract (both OpenAPI specs): every non-2xx response is
 * {"error": "<message>"}. We surface that as ApiError.serverMessage and keep a
 * Marathi, user-facing .message. When VITE_USE_MOCK=true, requests are served
 * by src/shared/api/mock instead of the network.
 * ========================================================================== */

export type Service = 'candidate' | 'admin';
export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';
export type Query = Record<string, string | number | boolean | undefined | null>;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** The backend's raw `error` string, if any (English). */
    public serverMessage: string | null = null,
    /** Seconds to wait, from a `Retry-After` header (sent with 429), if present. */
    public retryAfterSec: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ClientOptions {
  service: Service;
  baseUrl: string;
  getToken?: () => string | null;
  /** Called on any 401 — the admin portal uses it to send the user back to login. */
  onUnauthorized?: () => void;
}

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
}

export interface RawResponse {
  status: number;
  /** Parsed JSON body (or null) */
  data: unknown;
  /** For binary endpoints (export) */
  blob?: Blob;
  headers?: Headers;
}

export function buildQuery(query?: Query): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Retry-After is either delta-seconds ("120") or an HTTP date. */
function parseRetryAfter(h: Headers | undefined): number | null {
  const v = h?.get('Retry-After');
  if (!v) return null;
  if (/^\d+$/.test(v.trim())) return Number(v);
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : Math.max(0, Math.round((t - Date.now()) / 1000));
}

function serverError(data: unknown): string | null {
  if (data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string') {
    return (data as { error: string }).error;
  }
  return null;
}

export function genericMessage(status: number): string {
  if (status === 0) return 'सर्व्हरशी संपर्क होऊ शकला नाही. इंटरनेट जोडणी तपासून पुन्हा प्रयत्न करा.';
  if (status === 401) return 'सत्र संपले आहे किंवा लॉगिन तपशील चुकीचे आहेत.';
  if (status === 403) return 'ही कृती करण्याची परवानगी नाही.';
  if (status === 404) return 'मागितलेली माहिती सापडली नाही.';
  if (status === 409) return 'ही माहिती आधीपासून नोंदवलेली आहे.';
  if (status === 429) return 'खूप विनंत्या झाल्या आहेत. थोड्या वेळाने पुन्हा प्रयत्न करा.';
  if (status >= 500) return 'सर्व्हरमध्ये तांत्रिक अडचण आली. थोड्या वेळाने पुन्हा प्रयत्न करा.';
  return 'विनंती अयशस्वी झाली. भरलेली माहिती तपासून पुन्हा प्रयत्न करा.';
}

export function createClient(opts: ClientOptions) {
  async function raw(
    method: Method,
    path: string,
    o: RequestOptions & { expectBlob?: boolean } = {},
  ): Promise<RawResponse> {
    const token = opts.getToken?.() ?? null;

    // Inlined env check (not config.USE_MOCK) so Vite constant-folds it and the
    // mock module is dropped from production bundles entirely.
    if (import.meta.env.VITE_USE_MOCK === 'true') {
      const { mockRequest } = await import('./mock/handler.ts');
      return await mockRequest({
        service: opts.service,
        method,
        path,
        query: o.query ?? {},
        body: o.body,
        token,
      });
    }

    const headers: Record<string, string> = {};
    if (!o.expectBlob) headers.Accept = 'application/json';
    if (o.body !== undefined) headers['Content-Type'] = 'application/json';
    // Candidate API: spec says never send Authorization — getToken is undefined there.
    if (token) headers.Authorization = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${opts.baseUrl}${path}${buildQuery(o.query)}`, {
        method,
        headers,
        body: o.body !== undefined ? JSON.stringify(o.body) : undefined,
        signal: o.signal,
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      return { status: 0, data: null };
    }

    if (o.expectBlob && res.ok) {
      return { status: res.status, data: null, blob: await res.blob(), headers: res.headers };
    }
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text };
      }
    }
    return { status: res.status, data, headers: res.headers };
  }

  async function request<T>(method: Method, path: string, o: RequestOptions = {}): Promise<T> {
    const r = await raw(method, path, o);
    if (r.status === 401) opts.onUnauthorized?.();
    if (r.status < 200 || r.status >= 300) {
      throw new ApiError(r.status, genericMessage(r.status), serverError(r.data), parseRetryAfter(r.headers));
    }
    return r.data as T;
  }

  async function download(path: string, o: RequestOptions = {}) {
    const r = await raw('GET', path, { ...o, expectBlob: true });
    if (r.status === 401) opts.onUnauthorized?.();
    if (r.status < 200 || r.status >= 300 || !r.blob) {
      throw new ApiError(r.status, genericMessage(r.status), serverError(r.data));
    }
    const cd = r.headers?.get('Content-Disposition') ?? '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return { blob: r.blob, filename: m ? decodeURIComponent(m[1]) : null };
  }

  return {
    get: <T>(path: string, o?: RequestOptions) => request<T>('GET', path, o),
    post: <T>(path: string, body?: unknown, o?: RequestOptions) =>
      request<T>('POST', path, { ...o, body }),
    put: <T>(path: string, body?: unknown, o?: RequestOptions) =>
      request<T>('PUT', path, { ...o, body }),
    del: <T>(path: string, o?: RequestOptions) => request<T>('DELETE', path, o),
    download,
  };
}
